/**
 * Staff console client + pure logic for the Quixo expert review desk (track T10).
 * Server: /v1/audit-remediation/admin/expert/* (apps/api/src/modules/audit-remediation/expert/index.ts).
 * `:tenantId` is whatever the server returned (a raw tenant id, or a partner-client alias for Super Admins).
 */
import type { AdvancedFinding, AdvancedReport, ExpertNote } from '@qm/contracts/audit-remediation';
import type { Http } from '../kit/http';
import { seg } from '../kit/http';

export type QueueStatus = 'awaiting' | 'changes_requested' | 'approved' | 'all';
export type ReviewStatus = 'not_requested' | 'awaiting_payment' | 'awaiting_review' | 'changes_requested' | 'approved';
export type Tier = 'essential' | 'professional' | 'enterprise';
export interface Signoff { id: string; jobId: string; reviewerId: string; reviewerName: string; decision: 'approved' | 'changes_requested'; summary: { en: string; ar: string | null }; findingIds: string[]; signedAt: string }
export interface QueueRow {
  tenantId: string; jobId: string; businessName: string; domain: string | null; findingCount: number; generatedAt: string; tier: Tier; status: ReviewStatus;
  paidReview: boolean; lastSignoff: Signoff | null;
}
export interface Override { id: string; findingId: string; from: string; to: string; reason: string; by: string; at: string }
export interface ReviewJob {
  tenantId: string; report: AdvancedReport; tier: Tier;
  review: { status: ReviewStatus; included: boolean; purchase: { state: 'payment_pending' | 'paid' } | null };
  notes: ExpertNote[]; severityOverrides: Override[]; signoffs: Signoff[];
}
export type BookingStatus = 'requested' | 'payment_pending' | 'confirmed' | 'completed' | 'cancelled';
export interface StaffBooking {
  tenantId: string; id: string; jobId: string | null; findingId: string | null; topic: string; status: BookingStatus; scheduledAt: string | null; durationMin: number;
  priceMinor: number; currency: string; paymentRef: string | null; expertId: string | null; requestedBy: string; createdAt: string; updatedAt: string; preferredSlots: string[];
}
export type BookingAction = 'assign' | 'confirm' | 'complete' | 'cancel' | 'waive_payment';
export interface ThreadRow { id: string; jobId: string | null; findingId: string | null; title: string; createdAt: string; updatedAt: string; messageCount: number }
export interface ThreadMessage { id: string; role: 'user' | 'advisor' | 'expert'; authorId: string | null; body: string; citations: string[]; flags: string[]; createdAt: string }

const BASE = '/v1/audit-remediation/admin/expert';

export function expertAdminApi(http: Http) {
  const job = (t: string, j: string) => `${BASE}/tenants/${seg(t)}/jobs/${seg(j)}` as const;
  return {
    queue: (status: QueueStatus) => http.get<QueueRow[]>(`${BASE}/queue`, { status }),
    job: (t: string, j: string) => http.get<ReviewJob>(job(t, j)),
    addNote: (t: string, j: string, b: { findingId: string | null; body: string; visibility: 'internal' | 'customer' }) => http.post<ExpertNote>(`${job(t, j)}/notes`, b),
    updateNote: (t: string, j: string, id: string, b: { body?: string; visibility?: 'internal' | 'customer' }) => http.patch<ExpertNote>(`${job(t, j)}/notes/${seg(id)}`, b),
    overrideSeverity: (t: string, j: string, f: string, b: { severity: string; reason: string }) => http.post<Override>(`${job(t, j)}/findings/${seg(f)}/severity`, b),
    signoff: (t: string, j: string, b: { decision: Signoff['decision']; reviewerName: string; summary: { en: string; ar: string | null } }) => http.post<Signoff>(`${job(t, j)}/signoff`, b),
    grantReview: (t: string, j: string, reason: string) => http.post(`${job(t, j)}/review/grant`, { reason }),
    bookings: (status?: BookingStatus) => http.get<StaffBooking[]>(`${BASE}/bookings`, { status }),
    bookingAction: (t: string, id: string, b: { action: BookingAction; expertId?: string; scheduledAt?: string; reason?: string }) => http.patch<StaffBooking>(`${BASE}/tenants/${seg(t)}/bookings/${seg(id)}`, b),
    threads: (t: string, jobId?: string) => http.get<ThreadRow[]>(`${BASE}/tenants/${seg(t)}/advisor/threads`, { jobId }),
    thread: (t: string, id: string) => http.get<{ thread: ThreadRow; messages: ThreadMessage[] }>(`${BASE}/tenants/${seg(t)}/advisor/threads/${seg(id)}`),
    reply: (t: string, id: string, body: string) => http.post<ThreadMessage>(`${BASE}/tenants/${seg(t)}/advisor/threads/${seg(id)}/reply`, { body }),
  };
}
export type ExpertAdminApi = ReturnType<typeof expertAdminApi>;

// ---------- pure logic (mirrors the server; the server decides) ----------
export const SENIOR = ['staff_admin', 'staff_ops'];
export const NOTE_WRITERS = ['staff_admin', 'staff_ops', 'staff_content'];
export const canSignOff = (roles: readonly string[]) => roles.some((r) => SENIOR.includes(r));
export const canWriteNotes = (roles: readonly string[]) => roles.some((r) => NOTE_WRITERS.includes(r));

/** Severity after the latest expert override. */
export function effectiveSeverity(f: Pick<AdvancedFinding, 'id' | 'severity'>, overrides: readonly Override[]): { severity: string; overridden: boolean } {
  const o = overrides.find((x) => x.findingId === f.id);
  return o ? { severity: o.to, overridden: true } : { severity: f.severity, overridden: false };
}

export const BOOKING_ACTIONS_FROM: Record<BookingAction, readonly BookingStatus[]> = {
  assign: ['payment_pending', 'requested', 'confirmed'],
  confirm: ['requested'],
  complete: ['confirmed'],
  cancel: ['payment_pending', 'requested', 'confirmed'],
  waive_payment: ['payment_pending'],
};
export const bookingActionsFor = (status: BookingStatus, roles: readonly string[]): BookingAction[] =>
  canSignOff(roles) ? (Object.keys(BOOKING_ACTIONS_FROM) as BookingAction[]).filter((a) => BOOKING_ACTIONS_FROM[a].includes(status)) : [];

export function signoffProblems(f: { reviewerName: string; summaryEn: string }): Partial<Record<'reviewerName' | 'summaryEn', string>> {
  const out: Partial<Record<'reviewerName' | 'summaryEn', string>> = {};
  if (f.reviewerName.trim().length < 2 || f.reviewerName.trim().length > 80) out.reviewerName = 'audit-remediation.expert.admin.errors.reviewerName';
  if (!f.summaryEn.trim() || f.summaryEn.trim().length > 2000) out.summaryEn = 'audit-remediation.expert.admin.errors.summary';
  return out;
}

export const noteValid = (body: string) => body.trim().length >= 1 && body.trim().length <= 5000;

export const reviewTone = (s: ReviewStatus): 'good' | 'warn' | 'accent' | 'neutral' =>
  s === 'approved' ? 'good' : s === 'changes_requested' ? 'warn' : s === 'awaiting_review' ? 'accent' : 'neutral';
